import { useCallback, useEffect, useMemo, useState } from 'react';
import { getWsBaseUrl } from '../config/runtime';
import { authStorage } from '../services/authStorage';

// Singleton connection + registries (shared across hook instances)
let ws = null;
let connectionStatus = 'DISCONNECTED';
let isConnectedFlag = false;
let reconnectAttempts = 0;
let pingInterval = null;
let connectionTimeout = null;
let pongTimeout = null;
let isOnlineFlag = typeof navigator !== 'undefined' ? navigator.onLine : true;
// Observers to sync connection state to all hook instances
const observers = new Set(); // Set of functions (isConnected, status)

// Channels management
const subscribedChannels = new Set();
const channelRefCounts = new Map(); // channel -> refcount
// handlers: channel -> type -> Set(handlers)
const channelTypeHandlers = new Map();
// global handlers for connection-level messages (e.g., pong)
const globalTypeHandlers = new Map();
// Pending ack resolvers
const pendingSubscribeResolvers = new Map(); // channel -> Set(resolve)
const pendingUnsubscribeResolvers = new Map(); // channel -> Set(resolve)

// Helper to deliver messages to registered handlers
function dispatchMessage(msg) {
  try {
    const { type, data, channel } = msg || {};
    if (!type) return;
    // Heartbeat handling
    if (type === 'pong') {
      try { if (pongTimeout) { clearTimeout(pongTimeout); pongTimeout = null; } } catch (_) {}
    }
    const deliver = (handlersMap) => {
      if (!handlersMap.has(type)) return;
      const set = handlersMap.get(type);
      if (!set || set.size === 0) return;
      for (const fn of set) {
        try { fn(data || {}, msg); } catch (_) {}
      }
    };
    if (channel && channelTypeHandlers.has(channel)) {
      deliver(channelTypeHandlers.get(channel));
    }
    // Always also deliver to global if registered
    deliver(globalTypeHandlers);
    // Handle subscribe/unsubscribe acks
    if (type === 'subscribed') {
      const chans = (data && data.channels) || [];
      chans.forEach((ch) => {
        const resolvers = pendingSubscribeResolvers.get(ch);
        if (resolvers && resolvers.size) {
          resolvers.forEach((resolve) => { try { resolve(true); } catch (_) {} });
          pendingSubscribeResolvers.delete(ch);
        }
      });
    }
    if (type === 'unsubscribed') {
      const chans = (data && data.channels) || [];
      chans.forEach((ch) => {
        const resolvers = pendingUnsubscribeResolvers.get(ch);
        if (resolvers && resolvers.size) {
          resolvers.forEach((resolve) => { try { resolve(true); } catch (_) {} });
          pendingUnsubscribeResolvers.delete(ch);
        }
      });
    }
  } catch (_) {}
}

function ensureHandlerMaps(channel) {
  if (channel && !channelTypeHandlers.has(channel)) {
    channelTypeHandlers.set(channel, new Map());
  }
}

// Ensure we are subscribed to a channel and resolve once we receive the server ack
// Returns: Promise<boolean> (true if ack observed, false on timeout/fallback)
async function ensureSubscribed(channel) {
  if (!channel) return true;
  ensureHandlerMaps(channel);
  // If already tracked as subscribed and no pending ack, consider it ready
  if (subscribedChannels.has(channel) && !pendingSubscribeResolvers.has(channel)) {
    return true;
  }
  // Mark intent to subscribe
  if (!subscribedChannels.has(channel)) {
    subscribedChannels.add(channel);
  }
  // Send subscribe request if connection is open
  try {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'subscribe', data: { channels: [channel] } }));
    }
  } catch (_) {}
  // Prepare a resolver and safety timeout
  return await new Promise((resolve) => {
    const existing = pendingSubscribeResolvers.get(channel) || new Set();
    existing.add(resolve);
    pendingSubscribeResolvers.set(channel, existing);
    // Safety: don't block forever; fall back after 1500ms
    setTimeout(() => {
      const set = pendingSubscribeResolvers.get(channel);
      if (set && set.has(resolve)) {
        set.delete(resolve);
        if (set.size === 0) pendingSubscribeResolvers.delete(channel);
        resolve(false);
      }
    }, 1500);
  });
}

async function openConnection() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  if (!isOnlineFlag) return; // don't attempt while offline
  const base = getWsBaseUrl().replace(/\/$/, '');
  const token = await authStorage.getToken();
  if (!token) return;
  const url = `${base}/unified?token=${encodeURIComponent(token)}`;

  connectionStatus = 'CONNECTING';
  ws = new WebSocket(url);

  // Connection timeout safety
  if (connectionTimeout) clearTimeout(connectionTimeout);
  connectionTimeout = setTimeout(() => {
    try { ws && ws.close(); } catch (_) {}
  }, 10000);

  ws.onopen = () => {
    try { console.log('[WebSocket] connected'); } catch (_) {}
    clearTimeout(connectionTimeout);
    if (pongTimeout) { try { clearTimeout(pongTimeout); } catch (_) {} pongTimeout = null; }
    connectionStatus = 'CONNECTED';
    isConnectedFlag = true;
    reconnectAttempts = 0;
    observers.forEach((fn) => { try { fn(isConnectedFlag, connectionStatus); } catch (_) {} });
    // Resubscribe all channels
    if (subscribedChannels.size > 0) {
      const channels = Array.from(subscribedChannels);
      try {
        ws.send(JSON.stringify({ type: 'subscribe', data: { channels } }));
      } catch (_) {}
    }
    // Send client info once
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const locale = navigator.language || navigator.userLanguage || 'en-US';
      ws.send(JSON.stringify({ type: 'client_info', data: { timezone: tz, locale } }));
    } catch (_) {}
    // Start ping interval
    if (pingInterval) clearInterval(pingInterval);
    pingInterval = setInterval(() => {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', data: { timestamp: new Date().toISOString() } }));
          // Expect a pong; if not received, force-close to trigger reconnect
          if (pongTimeout) { try { clearTimeout(pongTimeout); } catch (_) {} }
          pongTimeout = setTimeout(() => {
            try { if (ws && ws.readyState === WebSocket.OPEN) { ws.close(); } } catch (_) {}
          }, 8000);
        }
      } catch (_) {}
    }, 20000);
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      dispatchMessage(msg);
    } catch (_) {}
  };

  ws.onerror = () => {
    try { console.warn('[WebSocket] error'); } catch (_) {}
    connectionStatus = 'ERROR';
    observers.forEach((fn) => { try { fn(isConnectedFlag, connectionStatus); } catch (_) {} });
  };

  ws.onclose = () => {
    try { console.log('[WebSocket] closed'); } catch (_) {}
    isConnectedFlag = false;
    connectionStatus = isOnlineFlag ? 'DISCONNECTED' : 'OFFLINE';
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    if (pongTimeout) { try { clearTimeout(pongTimeout); } catch (_) {} pongTimeout = null; }
    observers.forEach((fn) => { try { fn(isConnectedFlag, connectionStatus); } catch (_) {} });
    // Reconnect with backoff
    if (!isOnlineFlag) return; // wait until online
    const baseDelay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempts));
    const jitter = Math.floor(Math.random() * 500);
    const delay = Math.max(500, baseDelay) + jitter;
    reconnectAttempts += 1; // keep trying while online
    setTimeout(() => { openConnection(); }, delay);
  };
}

// Public hook API (per-channel optional)
export default function useUnifiedWebSocket(channelChatId = null) {
  const [isConnected, setIsConnected] = useState(isConnectedFlag);
  const [status, setStatus] = useState(connectionStatus);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(isOnlineFlag);

  // Streaming state scoped to channelChatId (chat channel only)
  const [streamingMessage, setStreamingMessage] = useState({ id: null, content: '', isComplete: false });
  const channelName = useMemo(() => channelChatId ? `chat_${channelChatId}` : null, [channelChatId]);

  // Open connection on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await openConnection(); } catch (e) { if (!cancelled) setError(e); }
      setIsConnected(isConnectedFlag);
      setStatus(connectionStatus);
    })();
    const observer = (con, st) => { setIsConnected(con); setStatus(st); };
    observers.add(observer);
    // Network listeners
    const onOnline = () => { isOnlineFlag = true; setIsOnline(true); try { openConnection(); } catch (_) {} };
    const onOffline = () => { isOnlineFlag = false; setIsOnline(false); };
    try { window.addEventListener('online', onOnline); } catch (_) {}
    try { window.addEventListener('offline', onOffline); } catch (_) {}
    // Visibility-based reconnect
    const onVisibility = () => { try { if (document.visibilityState === 'visible' && !isConnectedFlag) { openConnection(); } } catch (_) {} };
    try { document.addEventListener('visibilitychange', onVisibility); } catch (_) {}
    return () => { 
      cancelled = true; 
      try { window.removeEventListener('online', onOnline); } catch (_) {}
      try { window.removeEventListener('offline', onOffline); } catch (_) {}
      try { document.removeEventListener('visibilitychange', onVisibility); } catch (_) {}
    };
  }, []);

  // Subscribe to chat channel if provided
  useEffect(() => {
    if (!channelName) return;
    ensureHandlerMaps(channelName);
    // Track subscription
    if (!subscribedChannels.has(channelName)) {
      subscribedChannels.add(channelName);
      try { ws && ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'subscribe', data: { channels: [channelName] } })); } catch (_) {}
    }
    return () => {
      // Unsubscribe when hook instance unmounts or channel changes
      if (subscribedChannels.has(channelName)) {
        subscribedChannels.delete(channelName);
        try { ws && ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'unsubscribe', data: { channels: [channelName] } })); } catch (_) {}
      }
    };
  }, [channelName]);

  // Streaming handlers for this channel
  useEffect(() => {
    if (!channelName) return;
    ensureHandlerMaps(channelName);
    const onStreamChunk = (data) => {
      // Skip streaming messages with specific targets (like learn_card_*) - these are handled by InteractiveCards
      if (data?.stream_target) return;
      
      const chunk = data?.chunk || data?.content || '';
      const id = data?.message_id || `stream-${Date.now()}`;
      
      setStreamingMessage((prev) => {
        // 🔧 FIX: If message_id changed, start fresh (don't append to old message)
        // This prevents duplication when a new stream starts before the old one is cleared
        if (prev?.id && prev.id !== id) {
          return {
            id,
            content: chunk,
            isComplete: false,
            message_metadata: data?.message_metadata
          };
        }
        
        // Same message_id, append the chunk
        return {
          id: id || prev?.id || `stream-${Date.now()}`,
          content: `${prev?.content || ''}${chunk}`,
          isComplete: false,
          message_metadata: data?.message_metadata
        };
      });
    };
    const onStreamComplete = (data) => {
      // Skip streaming completion messages with specific targets (like learn_card_*) - these are handled by InteractiveCards
      if (data?.stream_target) return;
      
      const completionId = data?.message_id;
      
      setStreamingMessage((prev) => {
        // 🔧 FIX: Only mark complete if the message_id matches
        // This prevents marking the wrong message as complete
        if (prev?.id && completionId && prev.id !== completionId) {
          return prev; // Ignore completion for different message
        }
        return { ...(prev || {}), isComplete: true };
      });
      
      // 🔧 FIX: Reduced delay from 200ms to 50ms to minimize window for race conditions
      setTimeout(() => {
        setStreamingMessage((prev) => {
          // Only clear if still marked complete (not replaced by new stream)
          if (prev?.isComplete) {
            return { id: null, content: '', isComplete: false };
          }
          return prev;
        });
      }, 50);
    };
    // Register
    const typeMap = channelTypeHandlers.get(channelName);
    if (!typeMap.has('stream_chunk')) typeMap.set('stream_chunk', new Set());
    typeMap.get('stream_chunk').add(onStreamChunk);
    if (!typeMap.has('stream_complete')) typeMap.set('stream_complete', new Set());
    typeMap.get('stream_complete').add(onStreamComplete);
    return () => {
      try { typeMap.get('stream_chunk')?.delete(onStreamChunk); } catch (_) {}
      try { typeMap.get('stream_complete')?.delete(onStreamComplete); } catch (_) {}
    };
  }, [channelName]);

  // Expose register per-type handler scoped to a channel
  const registerHandler = useCallback((type, handler, channelOverride = channelName) => {
    const target = channelOverride || channelName;
    if (!type || typeof handler !== 'function') return;
    if (target) {
      ensureHandlerMaps(target);
      const typeMap = channelTypeHandlers.get(target);
      if (!typeMap.has(type)) typeMap.set(type, new Set());
      typeMap.get(type).add(handler);
      return () => { try { typeMap.get(type)?.delete(handler); } catch (_) {} };
    } else {
      // Global handler
      if (!globalTypeHandlers.has(type)) globalTypeHandlers.set(type, new Set());
      globalTypeHandlers.get(type).add(handler);
      return () => { try { globalTypeHandlers.get(type)?.delete(handler); } catch (_) {} };
    }
  }, [channelName]);

  const offMessage = useCallback((type, handler, channelOverride = channelName) => {
    const target = channelOverride || channelName;
    if (target) {
      const typeMap = channelTypeHandlers.get(target);
      try { typeMap && typeMap.get(type)?.delete(handler); } catch (_) {}
    } else {
      try { globalTypeHandlers.get(type)?.delete(handler); } catch (_) {}
    }
  }, [channelName]);

  const subscribeToChannel = useCallback((channel) => {
    if (!channel) return () => {};
    ensureHandlerMaps(channel);
    const current = channelRefCounts.get(channel) || 0;
    channelRefCounts.set(channel, current + 1);
    if (!subscribedChannels.has(channel)) {
      subscribedChannels.add(channel);
      try { ws && ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'subscribe', data: { channels: [channel] } })); } catch (_) {}
    }
    return () => {
      const prev = channelRefCounts.get(channel) || 1;
      const next = Math.max(0, prev - 1);
      if (next === 0) {
        channelRefCounts.delete(channel);
        subscribedChannels.delete(channel);
        try { ws && ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'unsubscribe', data: { channels: [channel] } })); } catch (_) {}
      } else {
        channelRefCounts.set(channel, next);
      }
    };
  }, []);

  const unsubscribeFromChannel = useCallback((channel) => {
    if (!channel) return;
    if (subscribedChannels.has(channel)) {
      subscribedChannels.delete(channel);
      try { ws && ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: 'unsubscribe', data: { channels: [channel] } })); } catch (_) {}
    }
  }, []);

  const sendMessage = useCallback(async (message, channelOverride = channelName) => {
    try {
      await openConnection();
      const payload = { ...(message || {}) };
      if (channelOverride && !payload.channel) {
        payload.channel = channelOverride;
      }
      // Guard: ensure channel is subscribed before sending messages that expect channel-scoped acks
      if (payload.channel) {
        try { await ensureSubscribed(payload.channel); } catch (_) {}
      }
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(payload));
      } else {
        // Fire-and-forget retry soon
        setTimeout(() => {
          try { ws && ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify(payload)); } catch (_) {}
        }, 250);
      }
    } catch (e) {
      setError(e);
    }
  }, [channelName]);

  const forceReconnect = useCallback(async () => {
    try {
      if (ws) {
        try { ws.close(); } catch (_) {}
      }
      await openConnection();
      setIsConnected(isConnectedFlag);
      setStatus(connectionStatus);
    } catch (e) {
      setError(e);
    }
  }, []);

  const sendPing = useCallback(() => {
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', data: { timestamp: new Date().toISOString() } }));
      }
    } catch (_) {}
  }, []);

  // Instance cleanup on unmount: release channels for this hook (none tracked here since subscribeToChannel returns disposer)
  useEffect(() => {
    return () => {
      // Remove this hook's observer if one was added
      // We added an anonymous observer above; not easily removable, but benign.
    };
  }, []);

  return {
    // connection
    isConnected,
    isOnline,
    connectionStatus: status,
    error,
    // channel control
    subscribeToChannel,
    unsubscribeFromChannel,
    // messaging
    sendMessage,
    sendPing,
    // handlers
    registerHandler,
    offMessage,
    // streaming (for chat channels)
    streamingMessage,
    forceReconnect
  };
}


